/**
 * "Upgrade your plan" cards — the subscription wizard's plan step. Replaced the old
 * `components/PlanComparisonTable.tsx`.
 *
 * What changed from the old table, and why:
 *
 * - Two ways to commit, on purpose. Clicking the CARD picks that plan and moves on — one action
 *   for someone who has already decided — and so does its CTA, which names the plan
 *   ("Continue with Pro Plus") and advances. Card and button agree, so there is no Next button
 *   under the grid and no way for the two to disagree.
 * - The CTA names the plan rather than the relationship to the current one (Downgrade / Stay with
 *   X / Upgrade). The button's COLOUR tracks the SELECTION: the chosen card is solid, every other
 *   card is outline (frosted on the recommended card and on a subscriber's own tier). It is never
 *   derived from the account's current plan — see `isSelectedCta` for why that kept regressing.
 * - Feature lists are cumulative ("Everything in Standard, and:") rather than repeating the
 *   lower tier's bullets, which is how the pricing page reads.
 * - No Monthly/Yearly toggle here: billing period moved to the next step, where the price
 *   difference can be shown against a real screen count.
 * - Free plan is a quiet line under the grid, not a bordered bar competing with the cards.
 */
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Check, ChevronUp, Info, Loader2, MoreHorizontal, Tag } from 'lucide-react';
import { Button, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, cn } from '@scio3/ui';
import { BILLING_INTERVAL } from '@scio3/shared-types';
import type { BillingInterval } from '@scio3/shared-types';
import { getCurrencySymbol, getPlanDisplayName } from '../../utils/plan-helpers';
import {
  getMonthlyEquivalent,
  getPlanFeatures,
  isSameAlias,
  usePlanCatalog,
} from './usePlanCatalog';
import {
  getPlanFeatureCopy,
  getPlanFeatureMore,
  getPlanTagline,
  toFallbackBullets,
} from './plan-feature-copy';

type Props = {
  onPlanChange: (plan: string) => void;
  /** Select and move on. Both the card body and its CTA do this. */
  /** Receives the alias just clicked, so the caller's plan-change validation does not read a
   *  `selectedPlan` that has not committed yet. */
  onContinue: (plan?: string) => void;
  /** Drives the chosen look (card outline) and which card shows the validating spinner. */
  selectedPlan?: string;
  currentPlan: string | undefined;
  /**
   * Trial accounts keep the current-plan card fully live.
   *
   * Two things hang off this, both on the card matching `currentPlan`: a trial gets the
   * "Current trial" chip in the card's top-right corner and a working Select button, because the
   * tier it is trialling is exactly what it is here to buy. A subscriber gets neither — its CTA states
   * "Your current plan" and is inert, so a chip repeating that would be the same fact twice.
   */
  isTrial?: boolean;
  currency: string;
  minPlanRequired?: string;
  hideFreePlanOption?: boolean;
  onSelectFree?: () => void;
  selectingFree?: boolean;
  /**
   * Rendered to the RIGHT of the Free-plan bar, on the same line — the step's forward button.
   *
   * A slot rather than this component owning the button: "next" is the step's business, but the
   * two have to share a row, and the bar's own content (the catalog-derived tooltip) is only
   * available here. Passing the button in is cheaper than lifting `usePlanCatalog` up, which is
   * a `no-cache` query and would fire twice.
   */
  footerSlot?: React.ReactNode;
  /** Server-side plan-change validation is in flight; shows the wait on the acted-on card. */
  validating?: boolean;
  /**
   * Which term the card prices are quoted for. The cards always show a PER-MONTH figure, so
   * yearly renders its monthly equivalent (`getMonthlyEquivalent`) with "billed yearly" beneath —
   * comparing €12.50/mo against €11.25/mo is the comparison the toggle exists to make, and
   * putting an annual lump sum on one card and a monthly one on the other would not be a
   * comparison at all.
   */
  billingInterval: BillingInterval;
  onBillingIntervalChange: (interval: BillingInterval) => void;
  /**
   * The currency control, rendered in this component's header row.
   *
   * It lives here rather than in WizardStepPlan so that it and the term toggle share ONE row
   * declared inside the same container as the grid — the toggle then lands on the left card's
   * left edge and the select on the right card's right edge by construction, instead of two
   * sibling rows that have to be kept in sync by hand.
   */
  currencySelect?: React.ReactNode;
};

export function PlanCardGrid({
  onPlanChange,
  onContinue,
  selectedPlan,
  currentPlan,
  isTrial = false,
  currency,
  minPlanRequired,
  hideFreePlanOption,
  onSelectFree,
  selectingFree = false,
  footerSlot,
  validating,
  billingInterval,
  onBillingIntervalChange,
  currencySelect,
}: Props) {
  const { t } = useTranslation();
  // Plans whose "And More" is open. Local UI state: it survives a re-render as the term or
  // currency changes, and resets when the step remounts.
  const [expandedPlans, setExpandedPlans] = useState<ReadonlySet<string>>(() => new Set());
  const toggleExpanded = (alias: string) =>
    setExpandedPlans((prev) => {
      const next = new Set(prev);
      if (next.has(alias)) next.delete(alias);
      else next.add(alias);
      return next;
    });
  const { plans, freePlan, loading } = usePlanCatalog(minPlanRequired);

  /**
   * What the Free plan costs you and what it keeps, for the downgrade line's tooltip.
   *
   * Same split the old `PlanComparisonTable` used: an UNCHECKED backend feature is a
   * limitation (3 screens, 25MB files, OptiSigns logo on screens), a CHECKED one is something
   * you still get. Read from the catalog rather than hardcoded so the list cannot drift from
   * what Free actually is — and capped at 4 limitations, matching the old table, because the
   * backend carries far more than fits a tooltip.
   */
  const freeLimitations = useMemo(
    () =>
      (freePlan?.features ?? [])
        .filter((f) => !f.isHide && !f.checked && f.description?.trim())
        .slice(0, 4)
        .map((f) => f.description ?? ''),
    [freePlan],
  );
  const freeIncluded = useMemo(
    () =>
      (freePlan?.features ?? [])
        .filter((f) => !f.isHide && f.checked && f.description?.trim())
        .map((f) => f.description ?? ''),
    [freePlan],
  );
  const hasFreeDetails = freeLimitations.length > 0 || freeIncluded.length > 0;

  /**
   * Select a plan and advance, swallowing the second half of a double-click.
   *
   * A card both selects and advances, so a double-click fired `onContinue` twice: the second call
   * lands while the step is still switching, which either skips a step or fires the downgrade
   * validation (and its warning dialog) twice for one intent.
   *
   * This is a short TIME window, not a once-per-mount latch. The latch assumed every click leaves
   * the step — but `doGoNext` deliberately stays put when it raises a downgrade warning, and the
   * user can dismiss that dialog. The component therefore never unmounted, the flag never reset,
   * and from then on the entire grid was dead to clicks with nothing on screen to explain why.
   * A window self-heals: it still cannot be double-fired, and a deliberate second click works.
   */
  const lastAdvanceAtRef = useRef(0);
  const selectAndAdvance = (alias: string) => {
    const now = Date.now();
    // Comfortably longer than a double-click (~500ms max), far shorter than a considered re-click.
    if (now - lastAdvanceAtRef.current < 600) return;
    lastAdvanceAtRef.current = now;
    onPlanChange(alias);
    // Alias passed explicitly: `onPlanChange` is a setState, so `selectedPlan` has not committed
    // by the time the wizard validates this advance. Without it every downgrade warning was one
    // click behind the card the user actually clicked.
    onContinue(alias);
  };
  const currencySymbol = getCurrencySymbol(currency);

  /**
   * Monthly / Yearly. A segmented control rather than two cards: the Screens step's period cards
   * carry per-term totals worth the space, whereas here the terms differ by one number that is
   * already printed on every card.
   */
  const termButton = (interval: BillingInterval, label: string, badge?: React.ReactNode) => {
    const isActive = billingInterval === interval;
    return (
      <button
        type="button"
        role="radio"
        aria-checked={isActive}
        onClick={() => onBillingIntervalChange(interval)}
        data-testid={`plan-term-${interval}`}
        /* A true segmented control: a white pill lifted out of a grey track.
           An earlier pass had the white pill with NO track, which is why it looked wrong — on a
           near-white ground the pill read as floating, and the shadow was the only thing saying
           it was a control at all. The track is what makes white mean "selected" here, so the
           two have to arrive together.
           `rounded-full` on both, matching the track, so the pill sits inside it rather than
           looking like a differently-shaped thing on top of it. */
        className={cn(
          'flex cursor-pointer items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
          isActive
            ? // No hover change: the pill IS the selection, and lightening it under the cursor
              // made it read as briefly unselected.
              'bg-background text-foreground shadow-sm'
            : // Hover darkens the TEXT only. A fill here would compete with the pill beside it
              // for the same "this one is chosen" meaning.
              'text-muted-foreground hover:text-foreground',
        )}
      >
        {label}
        {badge}
      </button>
    );
  };

  if (loading && plans.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Term toggle hard left, currency hard right, both inside the grid's own container so
          each lands on an outer card's edge. */}
      <div className="flex items-center justify-between gap-3">
        <div
          role="radiogroup"
          aria-label={t('Billing period')}
          /* The track. `panel-hover` (#eceef6) — the middle of the three neutrals, and the
             lightest one that still registers against this step's ground (#f5f6f8).
             `muted` (#f6f7fb) is invisible here, so the white pill would have nothing to sit in;
             `sidebar-active` (#e2e6f1) reads but is heavier than a track needs to be, since the
             pill's own contrast is what carries the selection.
             `p-1` is the inset that makes the pill read as lifted OUT of the track rather than
             laid over it. */
          className="flex items-center rounded-full bg-panel-hover p-1"
          data-testid="plan-term-toggle"
        >
          {termButton(BILLING_INTERVAL.MONTHLY, t('Monthly'))}
          {termButton(
            BILLING_INTERVAL.YEARLY,
            t('Yearly'),
            /* Tag glyph + the offer, in `text-primary`. The old pill put the same words in a
               tinted capsule INSIDE a pill that is itself the selected state — a chip on a chip.
               A glyph and coloured text carry it without a second container.

               A FLAT 10%, matching the `Save 10%` badge on the Screens step's Yearly card. This
               was briefly computed from the catalog instead, which surfaced a real inconsistency
               rather than a bug: GBP Standard is £7.95/mo against £83/yr, which is 13% off, so
               the badge read "13% off". The product's claim is 10% everywhere, so the UI states
               10% and the per-currency drift is a pricing-table question, not a display one. */
            <span className="flex items-center gap-1 text-primary">
              <Tag className="h-3.5 w-3.5 shrink-0" />
              <span className="text-sm font-semibold">{t('10% off')}</span>
            </span>,
          )}
        </div>
        {currencySelect}
      </div>
      {/* `auto-fit` + a 18rem floor: cards share the row equally while they all fit, and wrap
          onto the next row once they would go under 18rem — where the two-line feature labels
          start breaking badly. Replaces a hard `repeat(N, 1fr)`, which kept every plan on one
          row at any width and just let them get thinner and thinner. Also drops an inline
          style, which the component rules forbid. */}
      {/* Width capped by plan COUNT. `auto-fit` + 1fr makes every track share the row, so a
          single-plan set (an Upgrade Required context filtered by `minPlanRequired`) stretched one
          card across the full 72rem container — a lone card as wide as three, with its feature
          list floating in whitespace. The caps keep a card roughly the ~23rem it occupies in the
          three-up layout (72rem less two 1rem gaps, divided by three). Three or more keeps the
          parent's width and needs no cap. */}
      <div
        className={cn(
          'mx-auto grid grid-cols-[repeat(auto-fit,minmax(18rem,1fr))] gap-4',
          plans.length === 1 && 'max-w-sm',
          plans.length === 2 && 'max-w-3xl',
        )}
      >
        {plans.map((plan, index) => {
          const alias = plan.alias ?? '';
          const isRecommended = isSameAlias(alias, 'proPlus');
          // Only a tier whose list is a selection has more to reveal (see PLAN_FEATURE_MORE).
          const moreFeatures = getPlanFeatureMore(alias) ?? [];
          const hasMoreFeatures = moreFeatures.length > 0;
          const isExpanded = expandedPlans.has(alias);
          // Always a per-month figure — `getMonthlyEquivalent` divides a yearly rate by 12 — so
          // the "/screen/month" unit below stays true whichever term is selected.
          const price = getMonthlyEquivalent(plan, currency, billingInterval);
          const displayName = plan.name || getPlanDisplayName(alias);
          const previous = index > 0 ? plans[index - 1] : undefined;

          // Relationship to what they're on now. A trial or Free account has order 0, so
          // every paid tier reads as an upgrade and nothing claims to be their current plan.
          const allFeatures =
            getPlanFeatureCopy(alias) ??
            toFallbackBullets(getPlanFeatures(plan).map((f) => f.description));
          const shownFeatures = isExpanded ? [...allFeatures, ...moreFeatures] : allFeatures;

          const isSelected = !!selectedPlan && isSameAlias(alias, selectedPlan);
          /**
           * The tier the account is actually on right now. Every CTA now reads "Continue with
           * <plan>", so nothing in the button text distinguishes the plan they already have from
           * the two they don't — the badge carries it, and the CTA keeps reading as an action.
           *
           * Matched through `isSameAlias` because the catalog says `proPlus` and
           * `company.payment.planFeature` says `proplus`. Nothing matches for a Free or
           * expired-trial account (planFeature `free`, and Free has no card), or for a legacy
           * `pro` account (that card is retired) — those simply show no badge.
           */
          const isCurrentPlan = isSameAlias(alias, currentPlan);
          /**
           * The card for the plan a SUBSCRIBER is already paying for says so on its CTA, since the
           * badge that used to carry that fact is now trial-only. Trials are excluded here: a trial
           * user IS on Pro Plus but has never paid for it, so choosing it is the whole point of
           * this screen and its button reads "Select" like any other.
           */
          const isCurrentPlanLabel = isCurrentPlan && !isTrial;
          /**
           * ...and it only stops being an ACTION while that plan is also the selected one, where
           * clicking it would do nothing. Wander off to Standard and it becomes live again, because
           * it is then the way back to the plan you are on — the label is the destination, not a
           * disabled state.
           *
           * The card itself stays clickable throughout: that is the "keep my plan, change my screen
           * count" route into Customize, which the footer's Next also serves.
           */
          /**
           * NOTE there is deliberately no "inert current plan" state any more.
           *
           * The CTA used to be a selector, so on the plan you were already on it did nothing and
           * was disabled. It now CONTINUES to the next step, which is never a no-op — a subscriber
           * on Pro Plus goes through it to change screen count. With the footer's Next gone too,
           * disabling it would have left that card with no way forward at all.
           */
          /**
           * Whether the current plan's CTA gives up the primary fill.
           *
           * The green button should lead somewhere: on a subscriber's card it steps aside so the
           * tier ABOVE reads as the offer. But the top tier has nothing above it, so quieting that
           * one just ends the row on a washed-out card for the customer paying the most — there
           * `isTopTier` keeps it green even though it is still inert.
           *
           * `plans` arrives sorted ascending by `order` (usePlanCatalog) and already filtered by
           * `minPlanRequired`, so the last card is the highest tier actually on offer.
           *
           * Colour is now driven by selection (`isSelectedCta`), so no tier is permanently green
           * or permanently outline. This flag survives only to keep the top tier's INERT
           * current-plan treatment from washing out the card of the customer paying the most.
           */
          const isTopTier = index === plans.length - 1;
          /** Only the card being acted on shows the wait, so the other two stay readable. */
          const isValidatingThis = !!validating && isSelected;
          const isQuietCurrentPlan = isCurrentPlanLabel && !isTopTier;
          /**
           * CTA colour follows the SELECTION, not the tier.
           *
           * Green marks the card you have chosen; every other card is outline. The step then has
           * exactly one filled button at any moment, and it is always the one describing what
           * pressing Next will buy — so the fill answers "what am I about to get" rather than
           * "which tier does the page like", which the Recommended pill already says.
           *
           * NOTE this was tried once before and reverted for a reason that no longer holds: back
           * then Engage was solid, so an unselected Pro Plus going white read as disabled beside
           * it. Engage is outline now, so the row's resting state is uniformly outline and nothing
           * looks singled out.
           *
           * Still NOT derived from `currentPlan` or `isTrial` — that is what kept regressing (a
           * live Pro Plus trial reports `planFeature: 'proplus'`, so Pro Plus was not "above"
           * itself and silently lost its fill). `isSelected` is local UI state, so it cannot
           * reintroduce that.
           *
           * A subscriber's own tier is excluded: it is not something you select, so it keeps its
           * inert treatment even when it is the selected card.
           */
          const isSelectedCta = isSelected && !isCurrentPlanLabel;
          /**
           * Frosted, for an UNSELECTED recommended card and for a subscriber's own tier.
           * `bg-card/40` lets the card's mint wash read through the button instead of stamping a
           * plain white slab on the one tinted card — a flat outline button on that wash looked
           * like a hole punched in it. Neutral cards sit on white, where plain outline is already
           * right.
           */
          const isFrostedCta = isQuietCurrentPlan || (!isSelectedCta && isRecommended);

          return (
            <div
              key={plan._id}
              // Two ways to commit, on purpose. The CARD only marks the plan — it is a toggle, so
              // `aria-pressed` is how the state reads to a screen reader — and the footer's Next
              // is what moves on. Its own CTA is the shortcut: select and advance in one click,
              // for someone who has already decided.
              // Card-selects-and-advances was the previous behaviour; it left no way to pick a
              // plan and keep comparing, and made an accidental click on a large target commit
              // the whole step.
              role="button"
              aria-pressed={isSelected}
              tabIndex={0}
              data-testid={`plan-card-${alias || 'unknown'}`}
              onClick={() => onPlanChange(alias)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onPlanChange(alias);
                }
              }}
              className={cn(
                // Stays on shadow-sm (Level 1) throughout: hover darkens the shadow's COLOUR
                // but never its size or offset, so the card does not appear to lift. Note the
                // design system's blanket "no shadow on hover" rule is about elevation change;
                // this is a contrast change at a fixed elevation.
                // Sides stay on the standard 4; top and bottom get more, so the name is not
                // against the card's top edge and the last feature row is not on its bottom one.
                // `pt-8` (was `pt-6`): with the artwork gone the name sat close to the top rule.
                // The RECOMMENDED chip is absolutely positioned, so it does not move with this —
                // its `top-6` is set to match, keeping the same 8px it always sat above the name.
                // `isolate` creates a stacking context so the wash overlay below can sit at
                // -z-10: above this element's own background, beneath its content.
                'relative isolate flex cursor-pointer flex-col rounded-lg border bg-card px-6 pb-8 pt-8 shadow-sm',
                // Only border-colour and shadow move, so the transition names them explicitly
                // rather than reaching for transition-all.
                'transition-[border-color,box-shadow]',
                // Selection is the outline, not a fill: the recommended card already owns a green
                // wash, so a tint here could not tell "chosen" and "recommended" apart.
                isSelected ? 'border-primary' : 'border-border hover:border-input',
                'hover:shadow-black/20',
              )}
            >
              {/* Recommended tier's green wash, as its OWN layer rather than a background
                  utility on the card. Two reasons it has to be separate: cn()'s tailwind-merge
                  puts `bg-gradient-to-*` and `bg-card` in the same group, so adding the
                  gradient dropped the card's white background entirely (leaving it transparent
                  over the grey page); and a real element lets the fade stop partway along
                  without touching the card's own paint.
                  Runs DOWNWARD (`to-b`): the tint sits at the top behind the name and price, and
                  clears by 75% so the feature list below is on plain white.
                  Strength is `primary/20`, not `/10`. At 10% the fill resolves to #e8f7f4 — 90%
                  white, so almost no chroma survives and it reads grey rather than green. 20%
                  lands on #d1efea, which is unmistakably mint.
                  Anything placed in the tinted top band must be checked AGAINST #d1efea, not
                  against white — that is what the Recommended badge's dark text is for. */}
              {isRecommended && (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 -z-10 rounded-lg bg-gradient-to-b from-primary/20 to-transparent to-75%"
                />
              )}

              {/* The CORNER carries "Recommended"; "Current trial" sits beside the plan name
                  instead (below). Swapped back, deliberately: the corner is the card-wide slot, and
                  "Recommended" is the card-wide claim — it labels the tier, not the account, so it
                  belongs to the card rather than to the name.
                  Out of flow is what the corner buys either way: an absolutely positioned chip
                  cannot push the name row around however wide its label gets.
                  Soft `primary/20` fill, deliberately quiet — it is a label, not an action, so it
                  must not read as a second CTA beside the solid Select button. It sits INSIDE the
                  card's tinted top band, so its own tint stacks on the wash's and the pill still
                  reads as a distinct shape (~#ace3d9 against #d1efea), just softly. Text is
                  `primary-dark` (#0c5a4b), NOT `primary` (#1ab394): brand green on that doubled
                  tint is ~2:1, the faintest thing on the card, where the deeper green holds 5.7:1
                  even stacked. Contrast comes from the TEXT, weight from the fill. Dark mode flips
                  to `banner-text-dark`, since #0c5a4b is unreadable on a dark tint. */}
              {isRecommended && (
                <span
                  // `uppercase` + `tracking-wide`: caps at 11px with default tracking read as one
                  // dense block, and a chip label is short enough that the extra letter-spacing
                  // costs nothing. Done in CSS, not by shouting in the i18n string — translators
                  // get "Recommended", and languages where caps are wrong (or meaningless) can be
                  // handled by dropping the class rather than editing copy.
                  //
                  // `top-[34px]` centres the chip on the plan NAME's line box, and it is measured
                  // rather than chosen: the card's `pt-8` puts the `text-xl` h3 at 33px tall by
                  // 28px, so its centre is 47px down; the chip is 24.5px tall (py-1 + an 11px
                  // font's 16.5px line box), so it starts at 47 − 12.25 ≈ 34. A Tailwind step
                  // cannot hit that — `top-8` lands ~2px high. It is absolutely positioned (so a
                  // long translation can never push the name row around), which is exactly why the
                  // alignment has to be stated in pixels instead of falling out of a flex row.
                  // If `pt-8` on the card or the h3's size changes, re-measure this.
                  className="absolute right-6 top-[34px] whitespace-nowrap rounded-full bg-primary/20 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary-dark dark:text-banner-text-dark"
                >
                  {t('Recommended')}
                </span>
              )}

              {/* Just the name, and no wrapper row: the trial marker that used to share it moved OUT
                  of the cards entirely. It is a fact about the ACCOUNT, not about a tier, and inside
                  a card it made the trialled one taller than its neighbours while saying nothing
                  about how long the trial has left. It is now one line under the step's own title —
                  see `trialSubtitle` in SubscriptionWizardDialog. */}
              {/* No top margin: the card's own `pt-8` is the whole gap now that the artwork
                  above it is gone. */}
              <h3 className="text-xl font-semibold text-foreground">{displayName}</h3>

              {/* "Who is this for", before any pricing or features. Flush with the name now
                  the radio is gone.
                  Solid `text-foreground`, the same as the feature rows below — it is body copy
                  describing the tier, not a qualifier on the line above it, so it belongs with the
                  list rather than with the `/screen/month` unit. */}
              {getPlanTagline(alias) && (
                <p className="mt-1 text-sm text-foreground">{t(getPlanTagline(alias) as string)}</p>
              )}

              {/* Price — monthly per screen. The yearly saving is made on the next step. */}
              {/* No gap: the unit leads with a slash, so it has to sit tight against the
                  amount to read as one price ("$15.00/screen/month"). */}
              <div className="mt-3 flex items-baseline">
                {/* semibold, not bold: the size already carries the emphasis, and font-bold
                    here out-shouted the plan name above it. */}
                <span
                  className="text-4xl font-semibold text-foreground"
                  data-testid={`plan-price-${alias || 'unknown'}`}
                >
                  {currencySymbol}
                  {price.toFixed(2)}
                </span>
                {/* `text-sm`, the SAME size as the tagline under the plan name — the two are
                    the same kind of thing (a quiet label qualifying the line above), so they must
                    not be two different sizes. */}
                {/* `text-sm`, the SAME size as the tagline under the plan name — the two are
                    the same kind of thing (a quiet label qualifying the line above), so they must
                    not be two different sizes. No gap: the unit leads with a slash, so it has to
                    sit tight against the amount to read as one price. */}
                <span className="text-sm text-foreground/80">{t('/screen/month')}</span>
              </div>

              {/* Selects WITHOUT advancing — `stopPropagation` is what stops the card's own
                  click from carrying on to the next step. That split is the point: the button is
                  for someone still comparing, the card is for someone who has decided. */}
              {/* Colour is the plan relationship, NOT the selection — selection is carried by the
                  label below plus the card's own outline. Tying colour to selection turned Engage's
                  button white until it was clicked, which read as disabled next to Pro Plus. */}
              {/* Selects AND advances — the card body does the same thing, so the two targets no
                  longer disagree. It used to only mark the plan, which is why a separate Next
                  button existed underneath; naming the plan on the CTA ("Continue with Pro Plus")
                  makes that button redundant and the row's intent obvious without it.
                  `stopPropagation` still matters: without it the card's own handler would fire the
                  same advance a second time. */}
              <Button
                variant={isSelectedCta ? 'default' : 'outline'}
                className={cn(
                  // `mt-6`, not `mt-4`. The price is a `text-4xl` block whose descenders and the
                  // small `/screen/month` unit sit low in its line box, so 16px measured to the
                  // button's border read as about 10px of actual air and the two looked joined.
                  // This is also the card's only real seam now that the rule under the button is
                  // gone: price and action need to be legibly separate things.
                  'mt-6 w-full',
                  // Outline's hover fills the button while keeping its border, which left a hard
                  // stroke around the fill. Drop it so the hover reads as one soft solid shape.
                  !isSelectedCta && !isFrostedCta && 'hover:border-transparent',
                  // Frosted rather than filled: `bg-card` at 40% lets the card's own surface (white,
                  // or the recommended card's green wash) read through, which is what keeps this
                  // quiet without going grey. Explicitly NOT the Button's `:disabled` look
                  // (`bg-muted` + `text-muted-foreground`) — a grey slab was the heaviest thing on
                  // the card and made the plan the user actually pays for look like the one they
                  // can't have. Alpha on the TOKEN, not on white, so dark mode stays correct.
                  // Hover goes to FULL `bg-card` — it brightens toward the card surface rather
                  // than reaching for the outline variant's `hover:bg-accent` (#f7f7fb), which is
                  // a blue-grey and dirtied the button on the way up. Same token, 40% → 100%, so
                  // the move reads as "lighter", not "a different colour".
                  isFrostedCta && 'bg-card/40 hover:bg-card hover:text-foreground',
                )}
                data-testid={`plan-cta-${alias || 'unknown'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  selectAndAdvance(alias);
                }}
              >
                {/* The spinner belongs on the card that was clicked, not on a shared footer
                    button: leaving PLAN can hit server-side downgrade validation, and this is now
                    the only thing on screen that could be waiting. */}
                {isValidatingThis ? (
                  <>
                    <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />
                    {t('Checking...')}
                  </>
                ) : isCurrentPlanLabel ? (
                  /* A paying subscriber's own tier names itself rather than pitching a move to it.
                     Gated on `!isTrial`, since a trial's `planFeature` is the tier being trialled,
                     not one they pay for. */
                  t('Your current plan')
                ) : isSelected ? (
                  t('Selected')
                ) : (
                  /* Plain "Select", not "Continue with Pro Plus", and no arrow. The card's own
                     heading already names the plan, and an arrow promises forward motion that the
                     footer's Next now owns — the CTA does still advance, but as a shortcut rather
                     than as the step's only way on. */
                  t('Select')
                )}
              </Button>

              {/* No rule between the CTA and the feature list. There was an edge-to-edge hairline
                  here, from when the card also carried artwork and needed the price/CTA block
                  visibly fenced off from the list. With the artwork gone the card is short enough
                  to read as one column, and three vertical cards each cut in half by a horizontal
                  line made the grid read as six panels. The `mt-8` below is the whole separation
                  now — the same gap the rule used to sit in the middle of.
                  Cumulative feature list, so each card only states what it adds. The FIRST card has
                  no predecessor to name, so it gets a plain "Features include:" rather than
                  "<Plan> plan features:" — the heading sits directly under the plan name, which has
                  already said which tier this is. */}
              <p className="mt-8 text-sm text-foreground">
                {previous
                  ? t('Everything in {{plan}}, and:', {
                      plan: previous.name || getPlanDisplayName(previous.alias ?? ''),
                    })
                  : t('Features include:')}
              </p>
              {/* Never scrolls or clips: the card grows to its full list and the whole page
                  scrolls instead. `mt-4` matches `space-y-4` below, so the intro line sits at
                  the same rhythm as the rows rather than being tucked closer to the first one. */}
              <ul className="mt-4 flex-1 space-y-4">
                {/* Rows are `text-foreground`, NOT `text-muted-foreground` — grey body text was
                    the weakest thing on the card. All three cards take the solid colour so the
                    lists stay comparable, and the intro line above matches them.
                    The `/screen/month` unit is the one exception at `text-foreground/80`: grey,
                    but a step darker than `muted-foreground` (#6c757d), which washed out over the
                    recommended card's mint tint. There is no mid-grey token between `foreground`
                    and `muted-foreground`, hence the alpha. The tagline under the plan name used
                    to share that treatment and is now solid `text-foreground` with these rows —
                    it reads as body copy about the tier, not as a qualifier on the price.
                    Curated per-tier copy when we have it, catalog features otherwise — see
                    plan-feature-copy.ts for why the copy is not in the catalog. Curated rows
                    get their own icon; fallback rows keep a check mark, since picking an icon
                    for an unknown feature is not something this can do sensibly.
                    Icons are foreground, not primary: at this density a column of green
                    competed with the CTA for attention. Solid colours only — no alpha-faded
                    icons. */}
                {shownFeatures.map((feature, idx) => {
                  const Icon = feature.icon ?? Check;
                  return (
                    <li key={idx} className="flex items-start gap-3 text-sm text-foreground">
                      <Icon
                        aria-hidden="true"
                        className={cn(
                          'mt-0.5 h-4 w-4 shrink-0',
                          // Primary only on the recommended card: on a neutral card a green
                          // column competed with the CTA, but here it reinforces the tint.
                          isRecommended ? 'text-primary' : 'text-muted-foreground',
                        )}
                      />
                      <span>
                        {t(feature.label)}
                        {feature.tooltip && (
                          <TooltipProvider delayDuration={100}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                {/* stopPropagation on click AND keydown: the whole card is a
                                    button that selects the plan and advances the wizard, so
                                    without this, reading the caveat would commit the plan. */}
                                <button
                                  type="button"
                                  aria-label={t(feature.tooltip)}
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => e.stopPropagation()}
                                  className="ml-1 inline-flex cursor-pointer items-center justify-center align-middle text-muted-foreground transition-colors hover:text-foreground"
                                >
                                  <Info className="pointer-events-none h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-xs">
                                {t(feature.tooltip)}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </span>
                    </li>
                  );
                })}

                {/* Expands the rest of the list in place instead of sending the customer to the
                    pricing page mid-purchase. stopPropagation on click AND keydown: the whole
                    card is a button that selects the plan and advances the wizard. */}
                {hasMoreFeatures && (
                  <li className="flex items-start gap-3 text-sm text-foreground">
                    {isExpanded ? (
                      <ChevronUp
                        aria-hidden="true"
                        className={cn(
                          'mt-0.5 h-4 w-4 shrink-0',
                          isRecommended ? 'text-primary' : 'text-muted-foreground',
                        )}
                      />
                    ) : (
                      <MoreHorizontal
                        aria-hidden="true"
                        className={cn(
                          'mt-0.5 h-4 w-4 shrink-0',
                          // Primary only on the recommended card: on a neutral card a green
                          // column competed with the CTA, but here it reinforces the tint.
                          isRecommended ? 'text-primary' : 'text-muted-foreground',
                        )}
                      />
                    )}
                    <span>
                      {!isExpanded && <>{t('And')} </>}
                      <Button
                        type="button"
                        variant="link"
                        aria-expanded={isExpanded}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpanded(alias);
                        }}
                        onKeyDown={(e) => e.stopPropagation()}
                        className="h-auto cursor-pointer p-0 text-sm font-normal text-foreground underline hover:text-primary"
                        data-testid={`plan-more-features-${alias || 'unknown'}`}
                      >
                        {isExpanded ? t('Show less') : t('More')}
                      </Button>
                    </span>
                  </li>
                )}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Centred, and no longer a row shared with anything. It was pinned left with `mr-auto` to
          make room for the step's Next button on the same line. That button is back, so the row is
          right-aligned again: Next sits under the rightmost card's right edge whether or not the
          Free-plan line is there to share the row. `mr-auto` on that line (below) is what keeps it
          hard left in the same breath — `justify-end` alone would stack the two on the right, and
          `justify-between` would strand Next on the LEFT whenever the line is hidden, which is the
          case this is fixing. */}
      <div className="flex flex-wrap items-center justify-end gap-4">
        {/* Free plan, BELOW the cards: the question and the action side by side, matching
            the old `PlanComparisonTable` footer. Below the grid because it is the way OUT of
            choosing a plan — the opposite of the three cards.
            No border or fill: with the cards above it already boxed, one more outlined bar made
            the step read as four options. Plain text keeps it as the quiet way out, and the
            underlined link is still the thing the eye lands on.
            Inner `gap-2` is near a word-space: the question and the link are one sentence, and at
            `gap-4` they read as two separate items that happened to land on the same line. */}
        {!minPlanRequired && !hideFreePlanOption && onSelectFree && (
          <div className="mr-auto inline-flex flex-wrap items-center justify-center gap-2 px-4 py-3">
            <span className="text-sm text-foreground">
              {t("Don't want to keep your current features?")}
            </span>
            {/* Link and its explainer grouped: the icon documents the DOWNGRADE, not the
                question, so it belongs on the action's side of the row. */}
            <span className="inline-flex items-center gap-2">
              {/* Underlined text, not a bordered button: leaving the flow should not compete with
                  the three plan CTAs for the eye. */}
              <button
                type="button"
                disabled={selectingFree}
                onClick={onSelectFree}
                data-testid="plan-downgrade-free-btn"
                className={cn(
                  'inline-flex shrink-0 items-center text-sm font-medium text-foreground underline transition-colors',
                  selectingFree
                    ? 'cursor-not-allowed opacity-50'
                    : 'cursor-pointer hover:text-primary',
                )}
              >
                {selectingFree && (
                  <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />
                )}
                {t('Downgrade to Free Plan')}
              </button>
              {hasFreeDetails && (
                <TooltipProvider delayDuration={100} skipDelayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={t('Free plan details')}
                        data-testid="plan-free-details-tooltip-trigger"
                        className="-m-1 inline-flex shrink-0 cursor-help items-center justify-center p-1 text-muted-foreground transition-colors hover:text-foreground"
                      >
                        <Info className="pointer-events-none h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      className="max-w-xs"
                      data-testid="plan-free-details-tooltip"
                    >
                      <ul className="space-y-1 text-left">
                        {/* Limitations first: they are the reason to read this at all. */}
                        {freeLimitations.map((feature) => (
                          <li key={`limit-${feature}`} className="flex items-start gap-2">
                            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
                            <span>{feature}</span>
                          </li>
                        ))}
                        {freeIncluded.map((feature) => (
                          <li key={`incl-${feature}`} className="flex items-start gap-2">
                            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </span>
          </div>
        )}
        {footerSlot}
      </div>
    </div>
  );
}
